import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { aiAPI } from '../../lib/api';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { 
  MessageCircle, 
  X, 
  Send, 
  Minimize2,
  Maximize2,
  Bot,
  User,
  Loader2,
  Sparkles,
  HelpCircle,
  Scale,
  FileText,
  Briefcase,
  Lightbulb,
  RefreshCcw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';

const QUICK_PROMPTS = [
  { icon: HelpCircle, label: 'How do I use this app?', prompt: 'Give me a quick overview of how to use LEAP Pro for legal practice management.' },
  { icon: Scale, label: 'Legal research help', prompt: 'I need help with legal research. What resources and methods should I consider?' },
  { icon: FileText, label: 'Draft a document', prompt: 'Help me draft a professional legal document. What information do you need from me?' },
  { icon: Briefcase, label: 'Case strategy', prompt: 'I need help developing a case strategy. What factors should I consider?' },
];

export const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm your AI legal assistant. I can help you with:\n\n• **App questions** - How to use LEAP Pro features\n• **Case analysis** - Insights about your current cases\n• **Legal research** - General legal concepts and guidance\n• **Document drafting** - Help creating legal documents\n\nHow can I assist you today?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentCase, setCurrentCase] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Load current case context if on a case page
  useEffect(() => {
    const fetchCaseContext = async () => {
      const caseMatch = location.pathname.match(/\/cases\/([^/]+)/);
      if (caseMatch) {
        try {
          const response = await api.get(`/cases/${caseMatch[1]}`);
          setCurrentCase(response.data);
        } catch (error) {
          console.error('Failed to load case context:', error);
        }
      } else {
        setCurrentCase(null);
      }
    };
    fetchCaseContext();
  }, [location.pathname]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Build context based on current page and case
      let context = `User is currently on: ${location.pathname}`;
      
      if (currentCase) {
        context += `\n\nCurrent Case Context:
- Case Title: ${currentCase.title}
- Case Type: ${currentCase.case_type || 'Not specified'}
- Status: ${currentCase.status}
- Practice Area: ${currentCase.practice_area || 'Not specified'}
- Client: ${currentCase.client_name || 'Unknown'}
${currentCase.description ? `- Description: ${currentCase.description}` : ''}`;
      }

      // Add app context
      context += `\n\nApp Context: User is using LEAP Pro, a legal practice management system with features including client management, case tracking, document management, time billing, invoicing, calendar, and AI-powered case intelligence.`;

      const response = await aiAPI.chat(userMessage.content, context);
      
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    setInputValue(prompt);
    setTimeout(() => handleSend(), 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "Chat cleared! How can I help you?",
      timestamp: new Date()
    }]);
  };

  // Format message content with markdown-like styling
  const formatMessage = (content) => {
    return content
      .split('\n')
      .map((line, i) => {
        // Bold text
        line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        // Bullet points
        if (line.startsWith('• ') || line.startsWith('- ')) {
          return `<li key="${i}" class="ml-4">${line.substring(2)}</li>`;
        }
        return `<p key="${i}" class="${line ? '' : 'h-2'}">${line}</p>`;
      })
      .join('');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 p-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 group"
        data-testid="ai-chat-button"
      >
        <div className="relative">
          <MessageCircle className="h-6 w-6" />
          <Sparkles className="absolute -top-1 -right-1 h-3 w-3 text-yellow-300 animate-pulse" />
        </div>
        <span className="absolute -top-10 right-0 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          AI Legal Assistant
        </span>
      </button>
    );
  }

  return (
    <div 
      className={cn(
        "fixed z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col transition-all duration-300",
        isExpanded 
          ? "bottom-4 right-4 left-4 top-4 md:left-auto md:w-[600px] md:top-20" 
          : "bottom-6 right-6 w-[380px] h-[550px]"
      )}
      data-testid="ai-chat-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">AI Legal Assistant</h3>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {currentCase && (
            <Badge className="bg-white/20 text-white text-xs mr-2">
              Case: {currentCase.title?.substring(0, 15)}...
            </Badge>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearChat}
            className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/20"
            title="Clear chat"
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/20"
          >
            {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsOpen(false)}
            className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/20"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, index) => (
          <div 
            key={index}
            className={cn(
              "flex gap-3",
              msg.role === 'user' ? "flex-row-reverse" : ""
            )}
          >
            <Avatar className={cn(
              "h-8 w-8 flex-shrink-0",
              msg.role === 'user' ? "bg-slate-600" : "bg-gradient-to-br from-blue-500 to-indigo-600"
            )}>
              <AvatarFallback className="text-white">
                {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
              </AvatarFallback>
            </Avatar>
            <div className={cn(
              "max-w-[85%] rounded-2xl px-4 py-3 text-sm",
              msg.role === 'user' 
                ? "bg-blue-600 text-white rounded-tr-none" 
                : msg.isError 
                  ? "bg-red-50 text-red-700 border border-red-200 rounded-tl-none"
                  : "bg-slate-100 text-slate-800 rounded-tl-none"
            )}>
              <div 
                className="prose prose-sm max-w-none [&>p]:mb-1 [&>li]:mb-0.5 [&>ul]:list-disc [&>ul]:ml-4"
                dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
              />
              <p className={cn(
                "text-[10px] mt-2 opacity-60",
                msg.role === 'user' ? "text-blue-100" : "text-slate-400"
              )}>
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex gap-3">
            <Avatar className="h-8 w-8 bg-gradient-to-br from-blue-500 to-indigo-600">
              <AvatarFallback className="text-white">
                <Bot className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="bg-slate-100 rounded-2xl rounded-tl-none px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm text-slate-500">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Prompts - Show only when few messages */}
      {messages.length <= 2 && !isLoading && (
        <div className="px-4 pb-2">
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
            <Lightbulb className="h-3 w-3" />
            Quick questions:
          </p>
          <div className="flex flex-wrap gap-2">
            {QUICK_PROMPTS.map((prompt, i) => (
              <button
                key={i}
                onClick={() => handleQuickPrompt(prompt.prompt)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-50 hover:bg-slate-100 rounded-full text-slate-600 transition-colors"
              >
                <prompt.icon className="h-3 w-3" />
                {prompt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t bg-slate-50 rounded-b-2xl">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about law, your cases, or the app..."
            className="flex-1 bg-white border-slate-200 focus:border-blue-400 focus:ring-blue-400"
            disabled={isLoading}
            data-testid="ai-chat-input"
          />
          <Button 
            onClick={handleSend} 
            disabled={!inputValue.trim() || isLoading}
            className="bg-blue-600 hover:bg-blue-700 px-4"
            data-testid="ai-chat-send"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 mt-2 text-center">
          AI responses are for informational purposes only. Always verify with licensed attorneys.
        </p>
      </div>
    </div>
  );
};

export default AIChatWidget;
